'use client';

import { useState, useMemo } from 'react';
import RoomTravelersSelector from '@/components/search/RoomTravelersSelector';
import type { Room } from '@/types/travelers';

const PERSON_ICON = (
    <svg width="19" height="20" viewBox="0 0 19 20" fill="none" xmlns="http://www.w3.org/2000/svg" className="fill-alsero-dark-gray">
        <path d="M15.1811 7.77945C14.7379 9.41938 15.0146 11.6979 16.1374 12.9727C16.3996 13.2703 16.6426 13.5861 16.8643 13.9183C17.7646 15.2672 18.2809 16.8358 18.3576 18.4558C18.3678 18.6042 18.3483 18.7531 18.3002 18.8939C18.252 19.0346 18.1763 19.1643 18.0773 19.2754C17.9784 19.3865 17.8583 19.4767 17.724 19.5407C17.5898 19.6047 17.4441 19.6412 17.2955 19.6482C17.1469 19.6551 16.9984 19.6323 16.8588 19.5811C16.7191 19.5299 16.5911 19.4513 16.4823 19.3499C16.3734 19.2486 16.2859 19.1265 16.2249 18.9908C16.1638 18.8552 16.1305 18.7087 16.1268 18.56C16.0457 16.8394 15.305 15.216 14.0585 14.027C12.8121 12.838 11.1557 12.1747 9.43311 12.1747C7.71052 12.1747 6.05409 12.838 4.80767 14.027C3.56124 15.216 2.82057 16.8394 2.73938 18.56C2.71932 18.8513 2.58602 19.1231 2.36802 19.3173C2.15002 19.5115 1.86465 19.6126 1.57303 19.5989C1.28141 19.5853 1.00671 19.458 0.807769 19.2444C0.608827 19.0307 0.501452 18.7476 0.50864 18.4558C0.585226 16.836 1.10121 15.2675 2.00128 13.9186C2.22286 13.5865 2.46567 13.2708 2.72769 12.9732C3.85043 11.6981 4.12692 9.41954 3.68358 7.77945C3.36429 6.59825 3.4166 5.34736 3.83341 4.19695C4.25022 3.04653 5.01128 2.05243 6.01307 1.34986C7.01487 0.647302 8.20877 0.270386 9.43236 0.270386C10.656 0.270386 11.8499 0.647302 12.8517 1.34986C13.8534 2.05243 14.6145 3.04653 15.0313 4.19695C15.4481 5.34736 15.5004 6.59825 15.1811 7.77945ZM13.156 6.22244C13.156 5.23507 12.7638 4.28814 12.0656 3.58997C11.3674 2.89179 10.4205 2.49956 9.43311 2.49956C8.44574 2.49956 7.49881 2.89179 6.80064 3.58997C6.10247 4.28814 5.71024 5.23507 5.71024 6.22244C5.71024 7.2098 6.10247 8.15673 6.80064 8.8549C7.49881 9.55308 8.44574 9.94531 9.43311 9.94531C10.4205 9.94531 11.3674 9.55308 12.0656 8.8549C12.7638 8.15673 13.156 7.2098 13.156 6.22244Z" />
    </svg>
);

export interface GuestRoomSelectorBlockProps {
    rooms: Room[];
    onRoomsChange: (rooms: Room[]) => void;
    t: (key: string, fallback?: string) => string;
    getVariable: (key: string, fallback: string) => string;
    departureDate?: Date | null;
    maxRooms?: number;
    /** true ise dropdown yerine add room / oda listesi doğrudan form içinde görünür (örn. Book now modal) */
    alwaysExpanded?: boolean;
    /** Modal için kompakt görünüm (border ve arka plan diğer alanlarla uyumlu) */
    compactModal?: boolean;
}

/**
 * Rezervasyon ve talep formunda ortak kullanılan oda/yolcu seçim bloğu.
 * Kapalıyken özet + aç butonu, açıkken "X Adults, Y Child" + Done + RoomTravelersSelector.
 */
export default function GuestRoomSelectorBlock({
    rooms,
    onRoomsChange,
    t,
    getVariable,
    departureDate = null,
    maxRooms = 10,
    alwaysExpanded = false,
    compactModal = false,
}: GuestRoomSelectorBlockProps) {
    const [showRoomSelector, setShowRoomSelector] = useState(alwaysExpanded);

    const roomSummary = useMemo(() => {
        if (!rooms.length) return '';
        const totalAdults = rooms.reduce((sum, room) => sum + room.adults, 0);
        const totalChildren = rooms.reduce((sum, room) => sum + room.children, 0);
        const roomCount = rooms.length;
        const adultLabel = totalAdults === 1 ? getVariable('reservation.adult', 'Adult') : getVariable('reservation.adults', 'Adults');
        const childLabel = totalChildren === 1 ? getVariable('reservation.child', 'Child') : getVariable('reservation.children', 'Children');
        if (roomCount === 1) {
            return `${totalAdults} ${adultLabel}${totalChildren > 0 ? `, ${totalChildren} ${childLabel}` : ''}`;
        }
        const roomsLabel = getVariable('reservation.rooms', 'Rooms');
        const guestsLabel = getVariable('reservation.guests', 'Guests');
        return `${roomCount} ${roomsLabel}, ${totalAdults + totalChildren} ${guestsLabel}`;
    }, [rooms, getVariable]);

    const expandedContent = (
        <div className="space-y-2">
            <div className="flex flex-wrap items-center justify-between gap-2 mb-2">
                <label className="text-alsero-dark-gray text-sm font-medium">
                    {t('reservation.guest_count')}
                </label>
                {!alwaysExpanded && (
                    <div className="flex items-center gap-2">
                        <span className="text-alsero-dark-gray text-sm font-medium">
                            {roomSummary || t('reservation.select_guests')}
                        </span>
                        <button
                            type="button"
                            onClick={() => setShowRoomSelector(false)}
                            className="text-alsero-first text-xs font-medium hover:text-alsero-dark-gray"
                        >
                            {t('reservation.done')}
                        </button>
                    </div>
                )}
            </div>
            <RoomTravelersSelector
                rooms={rooms}
                onRoomsChange={onRoomsChange}
                title=""
                departureDate={departureDate ?? undefined}
                defaultOpen
                maxRooms={maxRooms}
                icon={PERSON_ICON}
                inline={alwaysExpanded}
                compactModal={compactModal}
            />
        </div>
    );

    if (alwaysExpanded) {
        return expandedContent;
    }

    if (!showRoomSelector) {
        return (
            <div>
                <label className="text-alsero-dark-gray text-sm font-medium mb-2 block">
                    {t('reservation.guest_count')}
                </label>
                <button
                    type="button"
                    onClick={() => setShowRoomSelector(true)}
                    className="w-full px-4 py-3 bg-white border border-alsero-soft-white rounded-lg text-left flex items-center justify-between hover:border-alsero-first transition-colors"
                >
                    <div className="flex items-center gap-2">
                        {PERSON_ICON}
                        <span className="text-alsero-dark-gray text-sm">
                            {roomSummary || t('reservation.select_guests')}
                        </span>
                    </div>
                    <svg width="12" height="8" viewBox="0 0 12 8" fill="none" xmlns="http://www.w3.org/2000/svg">
                        <path d="M1 1L6 6L11 1" stroke="#2E2D2D" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" />
                    </svg>
                </button>
            </div>
        );
    }

    return expandedContent;
}
